// components/AccountEmailThread.jsx

import React, { useState, useEffect } from "react";
import {
  Mail,
  Reply,
  Clock,
  User,
  ChevronDown,
  ChevronRight,
  CornerDownRight,
} from "lucide-react";
import apiService from "../services/api";
import ReplyAccountEmailModal from "./ReplyAccountEmailModal";
import { useTheme } from "../contexts/ThemeContext";

// Helper function to extract reply content from emails with quoted text
const extractReplyContent = (email) => {
  if (!email.body && !email.bodyHtml) return null;

  const isReply =
    email.providerData?.isIncomingReply ||
    email.metadata?.isIncomingReply ||
    email.metadata?.isReply ||
    email.providerData?.isReply;

  if (!isReply) {
    return email.bodyHtml || email.body;
  }

  let cleanContent = "";

  if (email.bodyHtml) {
    cleanContent = email.bodyHtml;
    cleanContent = cleanContent.replace(
      /<div class="gmail_quote[^"]*">[\s\S]*?<\/div>/gi,
      "",
    );
    cleanContent = cleanContent.replace(
      /<blockquote[\s\S]*?<\/blockquote>/gi,
      "",
    );
    cleanContent = cleanContent.replace(/On .+? wrote:[\s\S]*$/i, "");
    cleanContent = cleanContent.replace(
      /------- Original Message -------[\s\S]*$/i,
      "",
    );
    cleanContent = cleanContent.replace(/<div><br><\/div>/g, "");
    cleanContent = cleanContent.replace(/<div>\s*<\/div>/g, "");
    cleanContent = cleanContent.replace(/\n\s*\n\s*\n/g, "\n\n");

    const dirAutoMatch = cleanContent.match(/<div dir="auto">(.*?)<\/div>/i);
    if (dirAutoMatch && dirAutoMatch[1].trim()) {
      return dirAutoMatch[1].trim();
    }

    cleanContent = cleanContent.trim();
  }

  if (!cleanContent || cleanContent.length === 0) {
    cleanContent = email.body || "";

    const lines = cleanContent.split(/\r?\n/);
    const replyLines = [];

    for (const line of lines) {
      if (
        line.match(/^------- Original Message -------/i) ||
        line.match(/^On .+ wrote:/i) ||
        line.match(/^From:\s*\S+@/i) ||
        line.match(/^Date:/i) ||
        line.match(/^Subject:/i) ||
        line.match(/^\[Reply depth:/i) ||
        line.match(/^<html>/i) ||
        line.match(/^>{1,}\s*/)
      ) {
        break;
      }
      replyLines.push(line);
    }

    if (replyLines.length > 0) {
      cleanContent = replyLines
        .join("\n")
        .trim()
        .replace(/\n{3,}/g, "\n\n");
    }
  }

  if (cleanContent && cleanContent.length > 0) {
    return cleanContent;
  }

  return email.bodyHtml || email.body;
};

const EmailThreadItem = ({
  email,
  depth = 0,
  accountData,
  onReply,
  onRefresh,
}) => {
  const { isDarkMode } = useTheme();
  const [isExpanded, setIsExpanded] = useState(depth === 0);
  const maxDepth = 5;

  const formatDate = (date) => {
    const d = new Date(date);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString();
  };

  const getStatusColor = (status) => {
    const colors = {
      sent: "bg-blue-500/10 text-blue-400 border-blue-500/20",
      delivered: "bg-green-500/10 text-green-400 border-green-500/20",
      opened: "bg-purple-500/10 text-purple-400 border-purple-500/20",
      clicked: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
      replied: "bg-cyan-500/10 text-cyan-400 border-cyan-500/20",
      bounced: "bg-red-500/10 text-red-400 border-red-500/20",
      failed: "bg-red-500/10 text-red-400 border-red-500/20",
    };
    return colors[status] || "bg-gray-500/10 text-gray-400 border-gray-500/20";
  };

  const indentClass = Math.min(depth, maxDepth);
  const marginLeft = indentClass * 24;

  const displayContent = extractReplyContent(email);
  const isHtmlContent = email.bodyHtml && displayContent.includes("<");
  const isOutgoingReply = email.metadata?.isReply && email.sentBy;
  const isIncomingReply =
    email.metadata?.isIncomingReply || email.providerData?.isIncomingReply;

  return (
    <div style={{ marginLeft: `${marginLeft}px` }} className="mb-3">
      {depth > 0 && (
        <div
          className={`flex items-center gap-2 mb-2 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
        >
          <CornerDownRight size={16} />
          <span className="text-xs">Reply</span>
        </div>
      )}

      <div
        className={`border rounded-lg overflow-hidden transition-colors ${
          isDarkMode
            ? "bg-[#0C0E12] border-gray-700 hover:border-gray-600"
            : "bg-white border-gray-200 hover:border-gray-300"
        }`}
      >
        {/* Email Header */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className={`w-full p-4 text-left transition-colors ${
            isDarkMode ? "hover:bg-gray-800/30" : "hover:bg-gray-50"
          }`}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <Mail
                  className={`w-4 h-4 flex-shrink-0 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                />
                <span
                  className={`font-medium truncate ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  {email.subject}
                </span>
                <span
                  className={`text-xs px-2 py-0.5 rounded border flex-shrink-0 ${getStatusColor(email.status)}`}
                >
                  {email.status}
                </span>
              </div>

              <div
                className={`flex items-center gap-4 text-sm flex-wrap ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                <div className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  <span className="truncate">
                    {email.sender?.firstName && email.sender?.lastName
                      ? `${email.sender.firstName} ${email.sender.lastName}`
                      : email.from}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {formatDate(email.sentAt || email.createdAt)}
                </div>
                {email.replies && email.replies.length > 0 && (
                  <div className="flex items-center gap-1 text-blue-400">
                    <Reply className="w-3 h-3" />
                    {email.replies.length}{" "}
                    {email.replies.length === 1 ? "reply" : "replies"}
                  </div>
                )}
              </div>
            </div>

            {isExpanded ? (
              <ChevronDown
                className={`w-5 h-5 flex-shrink-0 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              />
            ) : (
              <ChevronRight
                className={`w-5 h-5 flex-shrink-0 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              />
            )}
          </div>
        </button>

        {/* Email Body */}
        {isExpanded && (
          <div
            className={`border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
          >
            <div className="p-4">
              <div className="mb-4 space-y-1 text-sm">
                <div className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
                  <strong
                    className={isDarkMode ? "text-gray-300" : "text-gray-700"}
                  >
                    From:
                  </strong>{" "}
                  {email.from}
                </div>
                <div className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
                  <strong
                    className={isDarkMode ? "text-gray-300" : "text-gray-700"}
                  >
                    To:
                  </strong>{" "}
                  {email.to}
                </div>
                {email.cc && email.cc.length > 0 && (
                  <div
                    className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                  >
                    <strong
                      className={isDarkMode ? "text-gray-300" : "text-gray-700"}
                    >
                      CC:
                    </strong>{" "}
                    {email.cc.join(", ")}
                  </div>
                )}
                {email.template && (
                  <div
                    className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                  >
                    <strong
                      className={isDarkMode ? "text-gray-300" : "text-gray-700"}
                    >
                      Template:
                    </strong>{" "}
                    {email.template.name}
                  </div>
                )}
                {isOutgoingReply && (
                  <div className="text-blue-400 text-xs">
                    <Reply className="w-3 h-3 inline mr-1" />
                    Outgoing reply
                    {email.metadata?.replyDepth &&
                      ` (depth: ${email.metadata.replyDepth})`}
                  </div>
                )}
                {isIncomingReply && (
                  <div className="text-green-400 text-xs">
                    <Reply className="w-3 h-3 inline mr-1" />
                    Incoming reply from customer
                  </div>
                )}
              </div>

              {isHtmlContent ? (
                <div
                  className={`prose prose-sm max-w-none ${isDarkMode ? "text-gray-300 prose-invert" : "text-gray-700"}`}
                  dangerouslySetInnerHTML={{ __html: displayContent }}
                />
              ) : (
                <div
                  className={`whitespace-pre-wrap ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                >
                  {displayContent}
                </div>
              )}

              {/* Reply Button */}
              <div
                className={`mt-4 pt-4 border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onReply(email);
                  }}
                  className="flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm"
                >
                  <Reply size={14} />
                  Reply
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Render nested replies */}
      {isExpanded && email.replies && email.replies.length > 0 && (
        <div className="mt-3">
          {email.replies.map((reply) => (
            <EmailThreadItem
              key={reply.id}
              email={reply}
              depth={depth + 1}
              accountData={accountData}
              onReply={onReply}
              onRefresh={onRefresh}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const AccountEmailThread = ({ accountId, accountData }) => {
  const { isDarkMode } = useTheme();
  const [threadData, setThreadData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyingTo, setReplyingTo] = useState(null);

  useEffect(() => {
    fetchThread();
  }, [accountId]);

  const fetchThread = async () => {
    try {
      setLoading(true);
      const response = await apiService.getAccountEmailThread(accountId);
      setThreadData(response.data);
    } catch (error) {
      console.error("Error fetching email thread:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleReplySuccess = () => {
    setReplyingTo(null);
    fetchThread();
  };

  if (loading) {
    return (
      <div
        className={`text-center py-8 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
      >
        Loading email thread...
      </div>
    );
  }

  if (!threadData || threadData.count === 0) {
    return (
      <div
        className={`text-center py-12 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
      >
        <Mail
          className={`w-12 h-12 mx-auto mb-3 ${isDarkMode ? "text-gray-600" : "text-gray-300"}`}
        />
        <p>No emails yet</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {/* Thread Statistics */}
        <div
          className={`flex items-center gap-4 text-sm pb-4 border-b ${
            isDarkMode
              ? "text-gray-400 border-gray-700"
              : "text-gray-500 border-gray-200"
          }`}
        >
          <div>
            <span
              className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              {threadData.totalEmails || threadData.count}
            </span>{" "}
            total emails
          </div>
          <div>
            <span
              className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              {threadData.count}
            </span>{" "}
            conversation{threadData.count !== 1 ? "s" : ""}
          </div>
        </div>

        {/* Email Threads */}
        {threadData.rows.map((email) => (
          <EmailThreadItem
            key={email.id}
            email={email}
            depth={0}
            accountData={accountData}
            onReply={setReplyingTo}
            onRefresh={fetchThread}
          />
        ))}
      </div>

      {/* Reply Modal */}
      {replyingTo && (
        <ReplyAccountEmailModal
          accountData={accountData}
          originalEmail={replyingTo}
          onClose={() => setReplyingTo(null)}
          onSuccess={handleReplySuccess}
        />
      )}
    </>
  );
};

export default AccountEmailThread;
